import type { ItemDef, Slot, Stats } from '../data/schema';
import { maxHpWith } from './meta/bonuses';
import { availableItems } from './meta/unlocks';
import { ZERO_STATS, type Player } from './types';
import type { World } from './world';

export interface ShopOffer { item: ItemDef; price: number; }
export interface ShopState { offers: (ShopOffer | null)[]; rerolls: number; rerollPrice: number; roomIndex: number; freeRerolls: number; }
export const SHOP_SIZE = 4;
export const REROLL_BASE = 25;

export function addStats(into: Stats, from: Partial<Stats>): void { for (const k of Object.keys(ZERO_STATS) as (keyof Stats)[]) into[k] += from[k] ?? 0; }

export function recomputeStats(world: World, p: Player): void {
  const c = world.content;
  const cls = c.classes[p.defId];
  if (!cls) return;
  const stats: Stats = { ...cls.baseStats };
  addStats(stats, p.passiveStats);
  const worn = Object.values(p.equipment).filter((id): id is string => !!id);
  for (const id of worn) { const it = c.items[id]; if (it) addStats(stats, it.stats); }
  for (const set of Object.values(c.sets)) {
    const n = worn.filter((id) => set.items.includes(id)).length;
    for (const b of set.bonuses) if (n >= b.count) addStats(stats, b.stats);
  }
  for (const b of p.buffs) if (b.until > world.time) addStats(stats, b.stats);
  p.stats = stats;
  const hpMax = maxHpWith(cls.hp + stats.vitalite, world.meta);
  const delta = hpMax - p.hpMax;
  p.hpMax = hpMax;
  p.hp = Math.min(hpMax, delta > 0 ? p.hp + delta : p.hp);
}

export function equipItem(world: World, p: Player, item: ItemDef): string | undefined {
  const replaced = p.equipment[item.slot];
  p.equipment[item.slot] = item.id;
  recomputeStats(world, p);
  return replaced;
}

function notWorn(world: World, p: Player): ItemDef[] { const worn = new Set(Object.values(p.equipment)); return availableItems(world.content, world.meta).filter((i) => !worn.has(i.id)); }

export function grantRandomItem(world: World, p: Player): ItemDef | null {
  const candidates = notWorn(world, p);
  if (!candidates.length) return null;
  const empty = candidates.filter((i) => !p.equipment[i.slot]);
  const pool = empty.length ? empty : candidates;
  const item = pool[world.rng.int(0, pool.length - 1)]!;
  equipItem(world, p, item);
  world.events.push({ type: 'itemGranted', itemId: item.id });
  return item;
}

/** Objet disponible de ce slot, non porté, tiré par world.rng puis équipé (objets de départ). */
export function grantItemOfSlot(world: World, p: Player, slot: Slot): ItemDef | null {
  const candidates = notWorn(world, p).filter((i) => i.slot === slot);
  if (!candidates.length) return null;
  const item = candidates[world.rng.int(0, candidates.length - 1)]!;
  equipItem(world, p, item);
  world.events.push({ type: 'itemGranted', itemId: item.id });
  return item;
}

export function priceFor(item: ItemDef, roomIndex: number, discountPct = 0): number { return Math.round(item.price * (1 + 0.15 * roomIndex) * (1 - discountPct / 100)); }

function draw(world: World, p: Player, roomIndex: number): (ShopOffer | null)[] {
  const pool = notWorn(world, p);
  const out: ShopOffer[] = [];
  while (out.length < SHOP_SIZE && pool.length) { const item = pool.splice(world.rng.int(0, pool.length - 1), 1)[0]!; out.push({ item, price: priceFor(item, roomIndex, world.meta.shopDiscountPct) }); }
  return out;
}
export function openShop(world: World, p: Player, roomIndex: number): ShopState { world.events.push({ type: 'shopOpened' }); return { offers: draw(world, p, roomIndex), rerolls: 0, rerollPrice: REROLL_BASE, roomIndex, freeRerolls: world.meta.shopRerollFree }; }
export function buy(world: World, p: Player, shop: ShopState, index: number): boolean {
  const o = shop.offers[index];
  if (!o || p.kamas < o.price) return false;
  p.kamas -= o.price;
  equipItem(world, p, o.item);
  shop.offers[index] = null;
  world.events.push({ type: 'bought', itemId: o.item.id, price: o.price });
  return true;
}
export function rerollShop(world: World, p: Player, shop: ShopState): boolean {
  if (shop.freeRerolls > 0) {
    // relance offerte par la méta : ni Kamas ni hausse du prix
    shop.freeRerolls -= 1;
    shop.offers = draw(world, p, shop.roomIndex);
    shop.rerolls += 1;
    world.events.push({ type: 'shopRerolled', price: 0 });
    return true;
  }
  if (p.kamas < shop.rerollPrice) return false;
  p.kamas -= shop.rerollPrice;
  world.events.push({ type: 'shopRerolled', price: shop.rerollPrice });
  shop.offers = draw(world, p, shop.roomIndex);
  shop.rerolls += 1;
  shop.rerollPrice *= 2;
  return true;
}
